import {
	TShopifyStoreConfig,
	TShopifyStoreSettingsResponse,
} from '../../../../../../redux/reducers/shopifyStores/types';
import { ETypeOfGoods, EWeightUnit, TEditShopifyStoreFormValues } from './types';

type TLooseRecord = Record<string, unknown>;

const asRecord = (value: unknown): TLooseRecord =>
	value && typeof value === 'object' ? (value as TLooseRecord) : {};

const asBool = (value: unknown): boolean => Boolean(value);
const asString = (value: unknown, fallback = ''): string =>
	value == null ? fallback : String(value);
const asNumber = (value: unknown, fallback = 0): number => {
	const num = Number(value);
	return Number.isFinite(num) ? num : fallback;
};

export const getEmptyInitialValues = (): TEditShopifyStoreFormValues => ({
	enabled: false,
	export_period: 0,
	immediately_process_paid_orders: false,
	weight_unit: EWeightUnit.GRAMS,
	act_as_disclosed_agent: false,
	hs6p_received: '',
	type_of_goods: ETypeOfGoods.GOODS,
	reduced_tbe_vat_group: false,
	seller_registration_country: '',
	originating_country: '',
	location_warehouse_country: '',
	order_tags_enable: false,
	handle_ireland_as_ioss: false,
	swisspost_full_ddp_delivery_customs_description: false,
	sales_channels_enabled: false,
	sales_channels_selected_will_be_processed: [],
});

export const getInitialValuesFromSettings = (
	settings: TShopifyStoreSettingsResponse | null,
): TEditShopifyStoreFormValues => {
	if (!settings) return getEmptyInitialValues();
	const general = asRecord(settings.config?.general);
	const exportSection = asRecord(general.export);
	const params = asRecord(general.params);
	const eas = asRecord(params.eas);
	const easParams = asRecord(eas.params);
	const salesChannels = asRecord(params.sales_channels);
	const selectedRaw = salesChannels.sales_channels_selected_will_be_processed;
	const selectedChannels = Array.isArray(selectedRaw)
		? (selectedRaw.filter((v) => Number.isFinite(Number(v))).map((v) => Number(v)) as number[])
		: [];
	const typeOfGoods = easParams.type_of_goods as ETypeOfGoods;

	return {
		enabled: asBool(general.enabled),
		export_period: asNumber(exportSection.period, 0),
		immediately_process_paid_orders: asBool(easParams.immediately_process_paid_orders),
		weight_unit: easParams.weight_unit as EWeightUnit,
		act_as_disclosed_agent: asBool(easParams.act_as_disclosed_agent),
		hs6p_received: asString(easParams.hs6p_received, ''),
		type_of_goods: typeOfGoods,
		reduced_tbe_vat_group: asBool(easParams.reduced_tbe_vat_group),
		seller_registration_country: asString(easParams.seller_registration_country, ''),
		originating_country: asString(easParams.originating_country, ''),
		location_warehouse_country: asString(easParams.location_warehouse_country, ''),
		order_tags_enable: asBool(easParams.order_tags_enable),
		handle_ireland_as_ioss: asBool(easParams.handle_ireland_as_ioss),
		swisspost_full_ddp_delivery_customs_description: asBool(
			easParams.swisspost_full_ddp_delivery_customs_description,
		),
		sales_channels_enabled: asBool(salesChannels.enabled),
		sales_channels_selected_will_be_processed: selectedChannels,
	};
};

/**
 * Merges form values into the loaded settings config so that only the
 * editable fields are overwritten and all other parts of the config are
 * preserved (the backend update endpoint validates the full payload).
 */
export const buildUpdateConfig = (
	settings: TShopifyStoreSettingsResponse | null,
	values: TEditShopifyStoreFormValues,
): TShopifyStoreConfig => {
	const base = settings?.config ? JSON.parse(JSON.stringify(settings.config)) : { general: {} };
	const general = asRecord(base.general);
	const exportSection = asRecord(general.export);
	const params = asRecord(general.params);
	const eas = asRecord(params.eas);
	const easParams = asRecord(eas.params);
	const salesChannels = asRecord(eas.sales_channels);

	general.enabled = values.enabled;
	general.export = { ...exportSection, period: values.export_period };

	eas.params = {
		...easParams,
		immediately_process_paid_orders: values.immediately_process_paid_orders,
		weight_unit: values.weight_unit,
		act_as_disclosed_agent: values.act_as_disclosed_agent,
		hs6p_received: values.hs6p_received === '' ? null : values.hs6p_received,
		type_of_goods: values.type_of_goods,
		reduced_tbe_vat_group: values.reduced_tbe_vat_group,
		seller_registration_country: values.seller_registration_country,
		originating_country: values.originating_country,
		location_warehouse_country: values.location_warehouse_country,
		order_tags_enable: values.order_tags_enable,
		handle_ireland_as_ioss: values.handle_ireland_as_ioss,
	};
	params.sales_channels = {
		...salesChannels,
		enabled: values.sales_channels_enabled,
		sales_channels_selected_will_be_processed: values.sales_channels_selected_will_be_processed,
	};

	params.eas = eas;
	general.params = params;
	base.general = general;
	return base as TShopifyStoreConfig;
};
